/**
 * PATH: packages/ui/src/hooks/useVisiblePolling.ts
 * FILE_ID: UI-HOOKS-VISIBLE-POLLING-001
 * PURPOSE: useVisiblePolling — run a callback on an interval ONLY while the
 *          tab is visible, plus an immediate fire on hidden→visible, with a
 *          gated window.focus refetch (BUG-0026 right-click noise guard).
 * WHY: Moved verbatim from packages/email-hub (useEmails.polling.ts, which is
 *      now a re-export) so the Diary day card can refresh planner edits with
 *      the same guarded cadence instead of a second copy (change #1386).
 * RELATED:
 *   - packages/email-hub/src/feature/hooks/useEmails.ts (inbox, 10s)
 *   - apps/workforce/src/pages/diary/DiaryDayCard.tsx (bookings, 60s)
 */
import { useEffect, useRef } from 'react'

/** Run `cb` every `intervalMs` only while the tab is visible. Also runs `cb`
 *  immediately the moment the tab transitions back to visible after being
 *  hidden, so a user who Cmd-Tabs away and back doesn't have to wait the full
 *  interval to see fresh data.
 *
 *  BUG-0026: previously also wired `window.focus` directly to `cbRef.current`
 *  which fired the polling callback on every bare focus event. Cursor's
 *  browser emits spurious focus events on right-click (because of installed
 *  native-dialog overrides), so a single right-click would queue an inbox
 *  refetch — multiplied across six identically-wired listeners across the
 *  email layer, that's what was making the email reader pane "move up and
 *  down" on right-click. The fix is to keep `visibilitychange` (the
 *  canonical "tab came back" signal) but require any `window.focus`-driven
 *  refetch to be preceded by a recent hidden transition. */
export function useVisiblePolling(cb: () => void, intervalMs: number) {
  const cbRef = useRef(cb)
  cbRef.current = cb

  useEffect(() => {
    let intervalId: number | null = null

    const start = () => {
      if (intervalId !== null) return
      intervalId = window.setInterval(() => {
        if (!document.hidden) cbRef.current()
      }, intervalMs)
    }
    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
    }
    let lastHiddenAt = 0
    const onVisibility = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now()
        stop()
      } else {
        cbRef.current()
        start()
      }
    }
    const onFocus = () => {
      // Only count a focus event as "user returned to tab" if the
      // tab actually went hidden in the recent past. Bare focus
      // events (right-click noise, popup steal-and-return) without
      // a prior hidden transition are ignored. 30 s window leaves
      // room for the OAuth redirect round-trip.
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt < 30_000) {
        cbRef.current()
      }
    }

    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [intervalMs])
}
